import {
  DSButton,
  DSDropdown,
  DSLinkButton,
} from "@components/design-system/molecules";
import {
  faSpinner,
  faPlus,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTeam } from "@hooks";
import React, { useState, useEffect } from "react";

import {
  generateExtensionPreviewText,
  parseExtensionToSteps,
} from "./extensionUtils";
import { isValidPhoneNumber } from "../../../../utils/phoneUtils";
import PhoneNumberInput from "../../../common/PhoneNumberInput";

const RequestStep = ({
  phoneNumber,
  onPhoneNumberChange,
  onRequestVerification,
  onCancel,
  onExtensionClick,
  onClearExtension,
  requesting,
  extension,
}) => {
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(false);
  const [selectedTeamUuid, setSelectedTeamUuid] = useState(null);
  const { userTeams } = useTeam();

  const teamOptions = [
    { label: "Don't share", value: "none" },
    ...(userTeams?.map((team) => ({
      label: team.name,
      value: team.uuid,
    })) || []),
  ];

  useEffect(() => {
    setIsPhoneNumberValid(isValidPhoneNumber(phoneNumber));
  }, [phoneNumber]);

  const handleVerifyClick = () => {
    onRequestVerification(selectedTeamUuid);
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-primary mb-2">
          Add Your Phone Number
        </h3>
        <p className="text-sm text-secondary">
          We'll call this number to verify it belongs to you
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-primary mb-2">
            Phone Number
          </label>
          <PhoneNumberInput
            value={phoneNumber}
            onChange={onPhoneNumberChange}
          />
        </div>
        <div className="space-y-2">
          <DSLinkButton onClick={onExtensionClick}>
            <FontAwesomeIcon icon={faPlus} className="text-xs" />
            {extension
              ? "Extension configured"
              : "My phone number has an extension code"}
          </DSLinkButton>
          {extension && (
            <div className="pl-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-secondary">
                  {generateExtensionPreviewText(
                    parseExtensionToSteps(extension),
                  )}
                </p>
                <button
                  className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 underline"
                  onClick={onClearExtension}
                >
                  Clear
                </button>
              </div>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-3 font-bold">
                ⚠️ Verification calls with extensions may take longer
                to reach you. If you don't receive a call after 2
                minutes, please try again or contact support.
              </p>
            </div>
          )}
        </div>
        {userTeams?.length > 0 && (
          <DSDropdown
            options={teamOptions}
            value={selectedTeamUuid ?? "none"}
            onChange={(val) =>
              setSelectedTeamUuid(val === "none" ? null : val)
            }
            label="Share with team"
            leadingIcon={faUsers}
            width="w-full"
            dataTestId="team-selector"
            renderOption={(option) => {
              if (option.value === "none") {
                return <span>{option.label}</span>;
              }
              const team = userTeams?.find(
                (t) => t.uuid === option.value,
              );
              return (
                <div className="flex flex-row items-center gap-xs">
                  <span>{option.label}</span>
                  <span className="text-xs text-secondary">
                    ({team?.users?.length || 0} members)
                  </span>
                </div>
              );
            }}
          />
        )}
      </div>

      <div className="w-full flex flex-row justify-center gap-sm">
        <DSButton variant="cancel" onClick={onCancel}>
          Cancel
        </DSButton>
        <DSButton
          onClick={handleVerifyClick}
          disabled={
            requesting || !phoneNumber.trim() || !isPhoneNumberValid
          }
        >
          {requesting ? (
            <FontAwesomeIcon
              icon={faSpinner}
              className="animate-spin"
            />
          ) : (
            "Verify"
          )}
        </DSButton>
      </div>
    </div>
  );
};

export default RequestStep;
